import { useMutation } from "@apollo/client";
import { XIcon } from "@heroicons/react/solid";
import topicDiscussed from "@static/img/topic-discussed.svg";
import topicUndiscussed from "@static/img/topic-undiscussed.svg";
import moment from "moment";
import { KeyboardEvent, useState } from "react";
import { TopicState } from "types/graphql-schema";
import type {
  CreateQuickTopicMutationMutation,
  CreateQuickTopicMutationMutationVariables,
  GetProfileOneononeQueryQuery,
} from "types/graphql-schema";

import { defaultTopicTitle } from "@apps/meeting-new/components/topic";
import deleteTopicMutation from "@apps/topic-dropdown-menu/graphql/delete-topic-mutation";
import useLabel from "@apps/use-label/use-label";
import cache from "@cache/cache";
import { currentUserVar, leftSidePanelVar } from "@cache/cache";
import removeTopic from "@cache/remove-topic";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { assertEdgesNonNull, getAssetUrl, getUrl } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import createQuickTopicMutation from "../graphql/create-quick-topic-mutation";

type NextOneonone = NonNullable<
  NonNullable<
    NonNullable<GetProfileOneononeQueryQuery["nextOneonone"]>["edges"][0]
  >["node"]
>;

const ProfileNextOneononeCard = ({
  meeting,
  profileId,
}: {
  meeting: NextOneonone;
  profileId: number;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const [topicTitle, setTopicTitle] = useState("");
  const [createTopic] = useMutation<
    CreateQuickTopicMutationMutation,
    CreateQuickTopicMutationMutationVariables
  >(createQuickTopicMutation);
  const [deleteTopic] = useMutation(deleteTopicMutation);

  const topics = meeting.topics ? assertEdgesNonNull(meeting.topics) : [];
  const hasOnlyDefaultTopic =
    topics.length === 1 && topics[0].title === defaultTopicTitle;
  const visibleTopics = hasOnlyDefaultTopic ? [] : topics;
  const meetingDate = meeting.startDatetime
    ? moment(meeting.startDatetime).format("ddd, MMM D")
    : "";
  const meetingTitle = meeting.title || label("1-on-1");

  const handleAddTopic = async () => {
    const title = topicTitle.trim();
    if (!title) return;

    setTopicTitle("");
    if (hasOnlyDefaultTopic) {
      // Update the existing default topic instead of creating a new one
      const { data } = await createTopic({
        variables: {
          topicId: topics[0].id,
          title,
        },
        optimisticResponse: {
          __typename: "Mutation",
          createOrUpdateTopic: {
            __typename: "CreateOrUpdateTopicMutation",
            topic: {
              __typename: "TopicNode",
              id: topics[0].id,
              title,
              state: TopicState.Open,
              canDelete: topics[0].canDelete,
            },
          },
        },
      });
      return data;
    }
    const tempId = -Date.now();
    const { data } = await createTopic({
      variables: {
        meetingId: meeting.id,
        title,
        assignee: currentUser.id,
      },
      optimisticResponse: {
        __typename: "Mutation",
        createOrUpdateTopic: {
          __typename: "CreateOrUpdateTopicMutation",
          topic: {
            __typename: "TopicNode",
            id: tempId,
            title,
            state: TopicState.Open,
            canDelete: { __typename: "PermissionNode", permission: true },
          },
        },
      },
      update: (_cache, { data }) => {
        const topic = data?.createOrUpdateTopic?.topic;
        if (!topic) return;
        const meetingCacheId = cache.identify(meeting);
        cache.modify({
          id: meetingCacheId,
          fields: {
            topics(existingTopics = { edges: [] }) {
              const topicRef = cache.identify(topic);
              const alreadyExists = existingTopics.edges.some(
                (edge: any) => edge.node.__ref === topicRef,
              );
              if (alreadyExists) return existingTopics;
              return {
                ...existingTopics,
                edges: [...existingTopics.edges, { node: { __ref: topicRef } }],
              };
            },
          },
        });
      },
    });
    return data;
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const data = await handleAddTopic();
      return data;
    }
  };

  const { confirm, ConfirmationDialog } = useConfirm(
    "Confirm",
    "Are you sure you want to delete this topic?",
  );
  const handleDeleteTopic = async (topicId: number) => {
    const confirmed = await confirm();
    if (confirmed) {
      await deleteTopic({
        variables: { topicId },
        update: () => {
          removeTopic({
            topic: { id: topicId },
            meeting,
          });
        },
      });
    }
  };

  const meetingUrl = getUrl({
    meetingGroupId: meeting.meetingGroupId,
    meetingId: meeting.id,
  });

  const viewAllUrl = `/meetings/1on1/user/${profileId}`;

  return (
    <div className="flex flex-col gap-6 border rounded-md p-6">
      <ConfirmationDialog />
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="tf-header-base-dark">
            Next {label("1-on-1", { titleCase: true })}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
            <span className="whitespace-nowrap">{meetingDate}</span>
            <span className="text-gray-300">•</span>
            <span className="truncate">{meetingTitle}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            theme={buttonTheme.text}
            size={ButtonSize.medium}
            to={viewAllUrl}
            onClick={(e) => {
              if (!e.metaKey) {
                e.preventDefault();
                leftSidePanelVar({
                  panel: "meetings",
                  type: "1on1",
                  userId: profileId,
                });
              }
            }}
          >
            View all {label("1-on-1")}s
          </Button>
          <Button
            theme={buttonTheme.primary}
            size={ButtonSize.medium}
            to={meetingUrl}
          >
            Open {label("1-on-1")}
          </Button>
        </div>
      </div>

      {/* Topics section */}
      <div className="flex flex-col gap-2">
        <div className="tf-header-sm">Topics</div>

        {visibleTopics.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {visibleTopics.map((topic) => (
              <div key={topic.id} className="flex items-start gap-2 group">
                <div className="mt-0.5 shrink-0">
                  {topic.state === TopicState.ClosedToDiscussion ? (
                    <img
                      src={getAssetUrl(topicDiscussed)}
                      alt="Discussed topic"
                      className="w-4 h-4"
                    />
                  ) : (
                    <img
                      src={getAssetUrl(topicUndiscussed)}
                      alt="Undiscussed topic"
                      className="w-4 h-4"
                    />
                  )}
                </div>
                <div className="flex-1 text-sm text-gray-600 min-w-0">
                  {topic.title}
                </div>
                {topic.state !== TopicState.ClosedToDiscussion &&
                  visibleTopics.length > 1 &&
                  topic.canDelete?.permission && (
                    <button
                      onClick={() => handleDeleteTopic(topic.id)}
                      className="shrink-0 p-0.5 rounded-sm text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
              </div>
            ))}
          </div>
        )}

        <input
          type="text"
          value={topicTitle}
          onChange={(e) => setTopicTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a topic title, Enter to add another"
          className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );
};

export default ProfileNextOneononeCard;
